/**
 * 2026-09-22 — Genel Bakış: operasyon kokpiti.
 *
 * Replaces the old mail.com-only KPI page. One bounded server aggregate
 * (GET /admin/overview, provider:view_health) feeds four blocks, top to bottom
 * (same order on every width):
 *   1. Sistem         orders 24h/7d, stuck orders, proxy/gateways, monitoring
 *   2. Sağlayıcılar   one equal-height card per server-registered provider
 *   3. Hızlı erişim   capability-filtered links
 *   4. Sorunlar       (2026-09-23: moved to the bottom) every server-ranked
 *                     problem in one compact, scrollable panel: severity chips
 *                     (Tümü/Kritik/Uyarı/Bilgi) + a provider filter, dense
 *                     rows with a one-click action. Because the list is last,
 *                     the page head carries an "n kritik sorun" chip that
 *                     jumps to it (#ov-problems) whenever critical > 0 and
 *                     switches the list to Kritik / Tüm sağlayıcılar, so the
 *                     landing list always shows what the chip counted.
 *
 * Every status rule lives on the server; this page only renders it. The data
 * is polled every 30s while visible and the page shows a warning whenever a
 * refresh failed or the data is older than 2× the poll interval — it never
 * sits silently stale. The only action ("Sorunluları tekrar dene") calls the
 * audited per-provider endpoint: server-picked dry run first, then confirm.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AdminCapability } from '@kopechka/contracts';
import { useApiMutation, useApiQuery, useVisiblePolling } from '../api/hooks.js';
import type { ApiClientOptions, ApiResult } from '../api/client.js';
import {
  fetchOverview,
  retryProblematic,
  type OverviewGateway,
  type OverviewProblem,
  type OverviewProvider,
  type OverviewResponse,
  type OverviewSeverity,
  type OverviewSiteFamilyDelivery,
  type RetryProblematicDryRun,
  type RetryProblematicResult,
} from '../api/overview.js';
import { useCan } from '../session/can.js';
import { useSession } from '../session/session-context.js';
import { useOverviewAlarms, type GlobalAlarmsState } from '../shell/app-shell.js';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Modal,
  PageHead,
  RatioBar,
  Select,
  Sparkline,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone, IconName, RatioBarTone } from '../ui/index.js';
import { ago } from '../i18n/activity-kinds.js';
import { loginFailureLabel } from '../i18n/login-failure-reasons.js';
import {
  GATEWAY_SOURCE_LABEL,
  GATEWAY_STATUS_LABEL,
  PROVIDER_STATUS_LABEL,
  SEVERITY_LABEL,
  WEBSHARE_STATUS_LABEL,
  describeProblem,
  overviewErrorHint,
  retryErrorText,
} from '../i18n/overview-problems.js';
import { providerLabel, providerLabels, providerPath } from '../providers/catalog.js';
import '../styles/dashboard.mobile.css';

/** Overview poll cadence; the stale warning fires after 2× this. */
export const OVERVIEW_POLL_MS = 30_000;
const STALE_AFTER_MS = OVERVIEW_POLL_MS * 2;
const RETRY_AUDIT_REASON = 'Genel Bakış: sorunlu hesapları tekrar dene';

type Tone = 'ok' | 'warn' | 'critical' | 'idle' | 'unknown';

/** In-page anchor of the (bottom) Sorunlar panel; the head chip jumps here. */
export const PROBLEMS_ANCHOR = 'ov-problems';
/** The scrollable list body inside the panel (reset to the top on a jump / filter change). */
const PROBLEMS_LIST_ID = 'ov-problems-list';

type SeverityFilter = 'all' | OverviewSeverity;
/** Provider-filter value for problems that belong to no provider ("Genel"). */
const GENERAL_FILTER = '__general__';
const ALL_PROVIDERS = '';

const SEVERITY_RANK: Record<OverviewSeverity, number> = { critical: 0, warn: 1, info: 2 };
const STATUS_RANK: Record<OverviewProvider['status'], number> = { critical: 0, warn: 1, ok: 2, idle: 3 };

function n(v: number): string {
  return v.toLocaleString('tr-TR');
}

function clock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function ageSeconds(sec: number): string {
  if (sec < 60) return `${Math.max(0, Math.round(sec))} sn`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} dk`;
  return `${Math.floor(min / 60)} sa ${min % 60} dk`;
}

/** Sort: critical → warn → info; server order kept within a severity. */
export function sortProblems(problems: readonly OverviewProblem[]): OverviewProblem[] {
  return problems
    .map((p, i) => ({ p, i }))
    .sort((a, b) => SEVERITY_RANK[a.p.severity] - SEVERITY_RANK[b.p.severity] || a.i - b.i)
    .map((x) => x.p);
}

/**
 * One row per condition. "Alarm e-postası ayarlı değil" (notifier_not_configured,
 * boot-time snapshot) and "Alarm e-postası bağlı değil" (notifier_unconfigured,
 * request-time evaluator view) describe the same thing; the server drops the
 * former when the latter is present, and this keeps the list clean against an
 * api that does not yet (UI and api are deployed separately).
 */
export function withoutDuplicateProblems(problems: readonly OverviewProblem[]): readonly OverviewProblem[] {
  if (!problems.some((p) => p.code === 'notifier_unconfigured')) return problems;
  return problems.filter((p) => p.code !== 'notifier_not_configured');
}

/**
 * In-app pages that refuse without a capability. A problem row never offers
 * an "Aç" that lands on a "yetkiniz yok" page (the overview itself only needs
 * provider:view_health). Same gates as the pages / Hızlı erişim.
 */
export function linkCapability(href: string): AdminCapability | null {
  const path = href.split(/[?#]/, 1)[0] ?? '';
  if (path.startsWith('/users/')) return 'user:view_detail';
  if (path === '/users') return 'user:list';
  if (path === '/orders' || path.startsWith('/orders/')) return 'order:inspect';
  return null;
}

/** Sort: critical first, then on-sale, then the rest (worse status first, then label). */
export function sortProviders(providers: readonly OverviewProvider[]): OverviewProvider[] {
  return [...providers].sort((a, b) => {
    const ac = a.status === 'critical' ? 0 : 1;
    const bc = b.status === 'critical' ? 0 : 1;
    if (ac !== bc) return ac - bc;
    if (a.onSale !== b.onSale) return a.onSale ? -1 : 1;
    const sr = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (sr !== 0) return sr;
    return providerLabel(a.providerId, a.label).localeCompare(providerLabel(b.providerId, b.label), 'tr');
  });
}

/** Site × domain table: rows listed before "Tümünü göster". */
export const SITE_SPLIT_TOP = 20;
/**
 * Below this many finished orders a row's rate is noise (0/1 is "0 %"): such
 * rows rank after every row with a real sample and are marked "az örnek", so a
 * dead pair like 0/16 is never pushed off the top by one-order sites.
 */
export const SITE_SPLIT_MIN_SAMPLE = 5;

/** One row of the Site × domain table: a (site, recipient domain) pair of one provider family. */
export interface SiteDomainRow {
  readonly key: string;
  readonly site: string;
  readonly providerId: string;
  /** The recipient domain, or the "diğer / tüm alan adları" label when `rest`. */
  readonly domain: string;
  /**
   * The family's orders outside its listed domains (the server lists a
   * bounded number of domains per site × family), so the rows still add up
   * to the family total.
   */
  readonly rest: boolean;
  readonly finished: number;
  readonly delivered: number;
  readonly ratePct: number;
  /** Last hour (closed_at); null for a `rest` row (the server sends no 1 h total per family). */
  readonly lastHour: { readonly finished: number; readonly delivered: number } | null;
  readonly lowSample: boolean;
}

/** Same rounding as the server's deliveryRatePct: floored, never 0 % once something was delivered. */
function splitRatePct(delivered: number, finished: number): number {
  if (finished <= 0) return 0;
  return Math.min(100, Math.max(delivered > 0 ? 1 : 0, Math.floor((delivered * 100) / finished)));
}

/** Worst first: real samples before "az örnek", then lowest rate, then most undelivered. */
function worstFirst(a: SiteDomainRow, b: SiteDomainRow): number {
  if (a.lowSample !== b.lowSample) return a.lowSample ? 1 : -1;
  // a.rate − b.rate without floating point (both finished > 0).
  const rate = a.delivered * b.finished - b.delivered * a.finished;
  if (rate !== 0) return rate;
  const undelivered = b.finished - b.delivered - (a.finished - a.delivered);
  if (undelivered !== 0) return undelivered;
  return (
    a.site.localeCompare(b.site, 'tr') ||
    a.providerId.localeCompare(b.providerId) ||
    Number(a.rest) - Number(b.rest) ||
    a.domain.localeCompare(b.domain)
  );
}

/**
 * Rows that would read identically are one row. The server keys a family by
 * (provider, site token), and every e-mail-shaped site shares one label
 * ("(e-posta gizlendi)") while keeping its own token, so two such families of
 * the same provider would otherwise show as look-alike rows with different
 * numbers. Plain sites never collide (their token is the site itself).
 */
function mergeLookAlikes(rows: readonly SiteDomainRow[]): SiteDomainRow[] {
  const byLabel = new Map<string, SiteDomainRow>();
  for (const r of rows) {
    const label = `${r.site}\u0000${r.providerId}\u0000${r.rest ? 1 : 0}\u0000${r.domain}`;
    const cur = byLabel.get(label);
    if (!cur) {
      byLabel.set(label, r);
      continue;
    }
    const finished = cur.finished + r.finished;
    const delivered = cur.delivered + r.delivered;
    byLabel.set(label, {
      ...cur,
      finished,
      delivered,
      ratePct: splitRatePct(delivered, finished),
      lastHour:
        cur.lastHour && r.lastHour
          ? {
              finished: cur.lastHour.finished + r.lastHour.finished,
              delivered: cur.lastHour.delivered + r.lastHour.delivered,
            }
          : (cur.lastHour ?? r.lastHour),
      lowSample: finished < SITE_SPLIT_MIN_SAMPLE,
    });
  }
  return [...byLabel.values()];
}

/**
 * Flattens the server's 24 h split (one entry per site × provider family, with
 * its recipient domains) into one row per (site, domain), sorted worst first.
 * The per-family rate hid a dead domain behind a healthy one; this does not.
 */
export function siteDomainRows(split: readonly OverviewSiteFamilyDelivery[]): SiteDomainRow[] {
  const rows: SiteDomainRow[] = [];
  split.forEach((f, i) => {
    let listedFinished = 0;
    let listedDelivered = 0;
    f.domains.forEach((d, j) => {
      listedFinished += d.finished;
      listedDelivered += d.delivered;
      if (d.finished <= 0) return; // only last-hour activity: no 24 h rate to show
      rows.push({
        key: `${i}:${j}`,
        site: f.site,
        providerId: f.providerId,
        domain: d.domain,
        rest: false,
        finished: d.finished,
        delivered: Math.min(d.delivered, d.finished),
        ratePct: d.ratePct,
        lastHour: { finished: d.finished1h, delivered: d.delivered1h },
        lowSample: d.finished < SITE_SPLIT_MIN_SAMPLE,
      });
    });
    const restFinished = f.finished - listedFinished;
    if (restFinished > 0) {
      const restDelivered = Math.max(0, Math.min(restFinished, f.delivered - listedDelivered));
      rows.push({
        key: `${i}:rest`,
        site: f.site,
        providerId: f.providerId,
        domain: f.domains.length === 0 ? 'tüm alan adları' : 'diğer alan adları',
        rest: true,
        finished: restFinished,
        delivered: restDelivered,
        ratePct: splitRatePct(restDelivered, restFinished),
        lastHour: null,
        lowSample: restFinished < SITE_SPLIT_MIN_SAMPLE,
      });
    }
  });
  return mergeLookAlikes(rows).sort(worstFirst);
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

function Dot({ tone, label }: { readonly tone: Tone; readonly label?: string }): JSX.Element {
  return (
    <span
      className={`ov-dot ov-dot-${tone}`}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      title={label}
    />
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DashboardPage(): JSX.Element {
  const canView = useCan('provider:view_health');
  return (
    <div className="ov-page">
      {canView ? (
        // Quick links render inside the cockpit so the Sorunlar panel can come last.
        <OverviewCockpit quickLinks={<QuickLinks />} />
      ) : (
        <>
          <PageHead
            crumb={<>Yönetim <Icon.ChevronRight size={11} /> Genel Bakış</>}
            title="Genel Bakış"
          />
          <EmptyState
            testId="ov-no-access"
            icon={<Icon.Lock size={22} />}
            label="Operasyon özetini görme yetkiniz yok."
            hint="Sağlayıcı sağlığı yetkisi (provider:view_health) gerekiyor. Aşağıdaki kısayolları kullanabilirsiniz."
          />
          <QuickLinks />
        </>
      )}
    </div>
  );
}

type RetryRequest = {
  readonly providerId: string;
  readonly confirm: boolean;
  readonly attempts?: 1 | 2 | 3;
};

const runRetry = (o: ApiClientOptions, i: RetryRequest): Promise<ApiResult<RetryProblematicResult>> =>
  retryProblematic(o, i.providerId, {
    confirm: i.confirm,
    ...(i.attempts !== undefined ? { attempts: i.attempts } : {}),
    reason: RETRY_AUDIT_REASON,
  });

type RetryFlow =
  | { readonly step: 'closed' }
  | { readonly step: 'loading'; readonly providerId: string }
  | { readonly step: 'preview'; readonly providerId: string; readonly preview: RetryProblematicDryRun }
  | { readonly step: 'applying'; readonly providerId: string; readonly preview: RetryProblematicDryRun }
  | {
      readonly step: 'error';
      readonly providerId: string;
      readonly message: string;
      readonly preview: RetryProblematicDryRun | null;
    };

interface Notice {
  readonly tone: 'success' | 'danger';
  readonly text: string;
}

/**
 * Smooth-scrolls (instant under reduced motion) to the Sorunlar panel, puts
 * its list back at the top and moves focus there (keyboard and screen-reader
 * users land on the list, not just the viewport).
 */
function scrollToProblems(target: HTMLElement): void {
  const list = document.getElementById(PROBLEMS_LIST_ID);
  if (list) list.scrollTop = 0;
  const reduceMotion =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
  target.focus({ preventScroll: true });
}

/**
 * Phones, tablets and any touch screen: the page is long there (the Sorunlar
 * list and the provider cards sit thousands of px below the top), so the
 * retry result notice at the top of the page would appear far off-screen.
 */
const NOTICE_REVEAL_MEDIA = '(max-width: 1080px), (hover: none) and (pointer: coarse)';

/**
 * View only: brings a freshly shown notice into view on small / touch screens
 * (instant under reduced motion) and moves focus to it; a notice that is
 * already fully on screen is only focused. Mouse desktops keep the old view.
 */
function revealNotice(el: HTMLElement): void {
  if (typeof window.matchMedia !== 'function' || !window.matchMedia(NOTICE_REVEAL_MEDIA).matches) return;
  const box = el.getBoundingClientRect();
  const clearTop = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  const onScreen = box.top >= clearTop && box.bottom <= window.innerHeight;
  if (!onScreen && typeof el.scrollIntoView === 'function') {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
  el.focus({ preventScroll: true });
}

function OverviewCockpit({ quickLinks }: { readonly quickLinks: ReactNode }): JSX.Element {
  const canManage = useCan('provider:manage_inventory');
  const alarms = useOverviewAlarms();
  // Sorunlar filters live here (not in the panel) so the head chip can set
  // them; they survive the 30s refresh either way.
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [providerFilter, setProviderFilter] = useState<string>(ALL_PROVIDERS);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const runner = useCallback(async (o: ApiClientOptions) => {
    const res = await fetchOverview(o);
    // Client-side proof of a fresh successful read. An identical payload keeps
    // the same query `version`, so freshness is tracked here, not from data.
    if (res.kind === 'ok') setFetchedAt(Date.now());
    return res;
  }, []);
  const query = useApiQuery<OverviewResponse>(runner, []);
  useVisiblePolling(query.reload, OVERVIEW_POLL_MS);
  const now = useNow(5_000);

  const [flow, setFlow] = useState<RetryFlow>({ step: 'closed' });
  const [attempts, setAttempts] = useState<1 | 2 | 3>(3);
  const [notice, setNotice] = useState<Notice | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const { run: runRetryMutation } = useApiMutation<RetryRequest, RetryProblematicResult>(runRetry);

  // A confirmed retry is usually started far down the page (Sorunlar, provider
  // cards) while its result lands at the top: on phones/tablets bring it into
  // view so the operator sees that it ran (and does not run it twice).
  useEffect(() => {
    if (notice?.tone === 'success' && noticeRef.current) revealNotice(noticeRef.current);
  }, [notice]);

  const data = query.kind === 'ok' ? query.data : null;
  const problems = useMemo(() => (data ? withoutDuplicateProblems(data.problems) : []), [data]);
  const criticalCount = problems.filter((p) => p.severity === 'critical').length;
  const providersById = useMemo(() => {
    const m = new Map<string, OverviewProvider>();
    for (const p of data?.providers ?? []) m.set(p.providerId, p);
    return m;
  }, [data]);

  // The chip promises the critical problems: land on exactly those, whatever
  // filter the operator left on earlier (Bilgi, one provider, ...).
  const jumpToProblems = useCallback((e: ReactMouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(PROBLEMS_ANCHOR);
    if (!target) return; // not rendered: the browser's own hash jump
    e.preventDefault();
    setSeverityFilter('critical');
    setProviderFilter(ALL_PROVIDERS);
    scrollToProblems(target);
  }, []);

  // Each open/close/confirm bumps the token; an async answer only lands if its
  // token is still current. Cancelling a slow dry run (it has no deadline, so
  // "Vazgeç" stays usable while it loads) must not reopen the modal, and a
  // late answer for provider A must never fill a modal opened for provider B.
  const retryToken = useRef(0);

  const openRetry = useCallback(
    async (providerId: string) => {
      const token = ++retryToken.current;
      setNotice(null);
      setAttempts(3);
      setFlow({ step: 'loading', providerId });
      const res = await runRetryMutation({ providerId, confirm: false });
      if (retryToken.current !== token) return;
      if (res.kind === 'ok' && res.data.dryRun) {
        setFlow({ step: 'preview', providerId, preview: res.data });
      } else if (res.kind === 'ok') {
        setFlow({ step: 'error', providerId, message: retryErrorText('invalid_retry_payload'), preview: null });
      } else {
        setFlow({
          step: 'error',
          providerId,
          message: res.kind === 'error' ? retryErrorText(res.reason) : 'Oturum sona erdi; tekrar giriş yapın.',
          preview: null,
        });
      }
    },
    [runRetryMutation],
  );

  const closeRetry = useCallback(() => {
    retryToken.current += 1;
    setFlow({ step: 'closed' });
  }, []);

  const confirmRetry = useCallback(async () => {
    if (flow.step !== 'preview') return;
    const { providerId, preview } = flow;
    const token = ++retryToken.current;
    setFlow({ step: 'applying', providerId, preview });
    const res = await runRetryMutation({ providerId, confirm: true, attempts });
    const current = retryToken.current === token;
    if (res.kind === 'ok' && !res.data.dryRun) {
      const label = providerLabel(providerId, providersById.get(providerId)?.label);
      const skipped = res.data.skippedInFlight > 0 ? `, ${n(res.data.skippedInFlight)} kullanımda olduğu için atlandı` : '';
      // The server applied it: report + reload even if the modal moved on.
      if (current) setFlow({ step: 'closed' });
      setNotice({
        tone: 'success',
        text:
          `${label}: ${n(res.data.nudged)} hesap yeniden denemeye alındı${skipped}. ` +
          'Sonuç ~1–15 dk içinde görünür.',
      });
      query.reload();
      return;
    }
    if (!current) return;
    setFlow({
      step: 'error',
      providerId,
      preview,
      message:
        res.kind === 'error'
          ? retryErrorText(res.reason)
          : res.kind === 'ok'
            ? retryErrorText('invalid_retry_payload')
            : 'Oturum sona erdi; tekrar giriş yapın.',
    });
  }, [flow, runRetryMutation, attempts, providersById, query]);

  // Freshness (never silently stale): a failed background refresh, an old
  // client-side read, or an old server-side cache all raise the warning.
  const clientAgeMs = fetchedAt !== null ? Math.max(0, now - fetchedAt) : null;
  const dataAgeSec = data ? data.cacheAgeSec + (clientAgeMs ?? 0) / 1000 : null;
  const stale =
    data !== null &&
    (query.refreshError !== null ||
      (clientAgeMs !== null && clientAgeMs > STALE_AFTER_MS) ||
      data.cacheAgeSec * 1000 > STALE_AFTER_MS);

  const subtitle = data ? (
    <span data-testid="ov-updated">
      Son güncelleme {clock(data.generatedAt)}
      {dataAgeSec !== null ? ` · ${ageSeconds(dataAgeSec)} önce` : ''} · her 30 sn yenilenir
    </span>
  ) : (
    'Sağlayıcı, sipariş ve altyapı durumunun tek ekranda özeti.'
  );

  return (
    <>
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Genel Bakış</>}
        title="Genel Bakış"
        subtitle={subtitle}
        actions={
          <>
            {criticalCount > 0 ? (
              <a
                href={`#${PROBLEMS_ANCHOR}`}
                className="ov-crit-chip"
                onClick={jumpToProblems}
                data-testid="ov-critical-chip"
                title="Sayfanın altındaki Sorunlar listesine git"
              >
                <Icon.Warn size={13} />
                {n(criticalCount)} kritik sorun
                <Icon.ArrowDn size={12} />
              </a>
            ) : null}
            <Button
              size="sm"
              icon={<Icon.Refresh size={14} />}
              loading={query.refreshing || query.kind === 'loading'}
              onClick={() => query.reload()}
              data-testid="ov-refresh"
            >
              Yenile
            </Button>
          </>
        }
      />

      {stale ? (
        <div className="ov-stale" data-testid="ov-stale">
          <Banner tone="warn" title="Veriler güncel olmayabilir">
            {query.refreshError !== null
              ? `Son yenileme başarısız: ${overviewErrorHint(query.refreshError) ?? query.refreshError}. `
              : ''}
            Gösterilen durum {dataAgeSec !== null ? `${ageSeconds(dataAgeSec)} önce` : 'bir süre önce'} alındı.
          </Banner>
        </div>
      ) : null}

      {notice ? (
        <div className="ov-notice" data-testid="ov-notice" ref={noticeRef} tabIndex={-1}>
          <Banner
            tone={notice.tone}
            action={
              <Button size="sm" variant="ghost" onClick={() => setNotice(null)} aria-label="Bildirimi kapat">
                <Icon.X size={12} />
              </Button>
            }
          >
            {notice.text}
          </Banner>
        </div>
      ) : null}

      {query.kind === 'loading' || query.kind === 'idle' ? (
        <LoadingState label="Genel bakış yükleniyor…" testId="ov-loading" />
      ) : query.kind === 'error' ? (
        query.reason === 'forbidden' ? (
          <EmptyState
            testId="ov-forbidden"
            icon={<Icon.Lock size={22} />}
            label="Operasyon özetini görme yetkiniz yok."
            hint={query.missingCapability ? `Eksik yetki: ${query.missingCapability}` : undefined}
          />
        ) : (
          <ErrorState
            testId="ov-error"
            reason={query.reason}
            hint={overviewErrorHint(query.reason)}
            onRetry={() => query.reload()}
          />
        )
      ) : query.kind === 'auth_required' ? (
        <ErrorState testId="ov-error" reason="auth_required" />
      ) : data ? (
        <>
          <SystemStrip data={data} alarms={alarms} />
          <ProvidersGrid providers={data.providers} canManage={canManage} onRetry={openRetry} />
        </>
      ) : null}

      {quickLinks}

      {data ? (
        <ProblemsPanel
          problems={problems}
          providersById={providersById}
          canManage={canManage}
          onRetry={openRetry}
          severity={severityFilter}
          onSeverity={setSeverityFilter}
          providerFilter={providerFilter}
          onProviderFilter={setProviderFilter}
        />
      ) : null}

      <RetryModal
        flow={flow}
        attempts={attempts}
        onAttempts={setAttempts}
        onClose={closeRetry}
        onConfirm={confirmRetry}
        provider={flow.step === 'closed' ? null : providersById.get(flow.providerId) ?? null}
        gatewayDown={
          flow.step !== 'closed' && providersById.get(flow.providerId)?.gateway?.status === 'down'
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 1) Sistem
// ---------------------------------------------------------------------------

function SystemStrip({
  data,
  alarms,
}: {
  readonly data: OverviewResponse;
  readonly alarms: GlobalAlarmsState;
}): JSX.Element {
  const sys = data.system;
  const o = sys.orders24h;
  const days = sys.ordersDaily7d;
  const stuck = sys.stuckOrders.expiredLease + sys.stuckOrders.oldReserved;
  const deliveredPct = o.total > 0 ? Math.round((o.delivered / o.total) * 100) : null;
  const deliveryTone: RatioBarTone =
    deliveredPct === null ? 'neutral' : deliveredPct >= 60 ? 'success' : deliveredPct >= 30 ? 'warn' : 'danger';
  const webshareTone: Tone =
    sys.webshare.status === 'ok' ? 'ok' : sys.webshare.status === 'warn' ? 'warn' : sys.webshare.status === 'down' ? 'critical' : 'unknown';
  // A failed alarms refresh means the last answer is not current: "unknown", never "Çalışıyor".
  const evaluator = alarms.stale ? null : alarms.data;
  const evaluatorTone: Tone = evaluator === null ? 'unknown' : evaluator.evaluatorAlive ? 'ok' : 'critical';
  const evaluatorText =
    alarms.stale ? 'Okunamadı' : evaluator === null ? 'Bilinmiyor' : evaluator.evaluatorAlive ? 'Çalışıyor' : 'Yanıt vermiyor';

  return (
    <section className="ov-section ov-section--system" aria-labelledby="ov-system-title">
      <div className="ov-section-head">
        <h2 id="ov-system-title" className="ov-section-title">
          Sistem
        </h2>
      </div>
      <div className="ov-system">
        <div className="ov-tile" data-testid="ov-tile-orders">
          <div className="ov-tile-label">
            <Icon.Inbox size={14} /> Siparişler · 24 saat
          </div>
          <div className="ov-tile-value t-num">{n(o.total)}</div>
          <div className="ov-tile-meta">
            <span className="ov-good">{n(o.delivered)} teslim</span> · {n(o.cancelled)} iptal · {n(o.open)} açık
          </div>
          <RatioBar value={o.delivered} total={o.total} tone={deliveryTone} label="Teslim oranı" testId="ov-orders-ratio" />
          <div className="ov-tile-spark">
            <Sparkline
              fluid
              values={days.map((d) => d.total)}
              overlay={days.map((d) => d.delivered)}
              labels={days.map((d) => d.day)}
              valueName="sipariş"
              overlayName="teslim"
              width={168}
              height={34}
              ariaLabel={`Son ${days.length} gün sipariş: ${days.map((d) => `${d.day} ${d.total}/${d.delivered}`).join(', ')}`}
              testId="ov-orders-spark"
            />
            <span className="ov-muted ov-xs">Son 7 gün (UTC) · yeşil = teslim</span>
          </div>
        </div>

        <div className={`ov-tile${stuck > 0 ? ' ov-tile-bad' : ''}`} data-testid="ov-tile-stuck">
          <div className="ov-tile-label">
            <Icon.Clock size={14} /> Takılı siparişler
          </div>
          <div className="ov-tile-value t-num">{n(stuck)}</div>
          <ul className="ov-kv">
            <li>
              <span>Kira süresi dolmuş, kapanmamış</span>
              <strong className="t-num">{n(sys.stuckOrders.expiredLease)}</strong>
            </li>
            <li>
              <span>Mail gelmiş, 15 dk'dan uzun süredir açık</span>
              <strong className="t-num">{n(sys.stuckOrders.oldReserved)}</strong>
            </li>
          </ul>
        </div>

        <div className="ov-tile" data-testid="ov-tile-proxy">
          <div className="ov-tile-label">
            <Icon.Globe size={14} /> Proxy
          </div>
          {data.gateways.length === 0 ? (
            <div className="ov-muted ov-xs">Konut ağ geçidi tanımlı değil.</div>
          ) : (
            <ul className="ov-gateways">
              {data.gateways.map((g) => (
                <GatewayRow key={`${g.host}:${g.port}:${g.providerIds.join(',')}`} gateway={g} />
              ))}
            </ul>
          )}
          <div className="ov-kv-line" data-testid="ov-webshare">
            <Dot tone={webshareTone} />
            <span>Webshare havuzu</span>
            <strong className="t-num">{sys.webshare.poolSize === null ? '—' : n(sys.webshare.poolSize)}</strong>
            <span className="ov-muted ov-xs">{WEBSHARE_STATUS_LABEL[sys.webshare.status]}</span>
          </div>
        </div>

        <div className="ov-tile" data-testid="ov-tile-monitoring">
          <div className="ov-tile-label">
            <Icon.Bell size={14} /> Ölçüm ve alarm servisi
          </div>
          <ul className="ov-checks">
            <li>
              <Dot tone={sys.metricsAvailable ? 'ok' : 'critical'} />
              <span>Ölçümler (Prometheus)</span>
              <strong>{sys.metricsAvailable ? 'Bağlı' : 'Ulaşılamıyor'}</strong>
            </li>
            <li>
              <Dot tone={sys.runtime.metricsFresh === null ? 'unknown' : sys.runtime.metricsFresh ? 'ok' : 'warn'} />
              <span>Runtime ölçümleri</span>
              <strong>
                {sys.runtime.metricsFresh === null ? 'Bilinmiyor' : sys.runtime.metricsFresh ? 'Geliyor' : 'Gelmiyor'}
              </strong>
            </li>
            <li data-testid="ov-evaluator">
              <Dot tone={evaluatorTone} />
              <span>Alarm servisi</span>
              <strong>{evaluatorText}</strong>
            </li>
            <li data-testid="ov-notifier">
              <Dot tone={sys.notifier.emailConfigured ? 'ok' : 'warn'} />
              <span>E-posta bildirimi</span>
              <strong>{sys.notifier.emailConfigured ? 'Bağlı' : 'Bağlı değil'}</strong>
            </li>
          </ul>
        </div>
      </div>
      <SiteDomainTable split={sys.deliveryBySiteFamily24h} />
    </section>
  );
}

/**
 * 2026-09-23 — "Site × domain (24 saat)": the server's per site × provider
 * family split, one row per (site, recipient domain), worst first. The daily
 * total alone hid which pairs were dead (e.g. discord.com × mail.com at 0 %
 * while the day read fine). Display only — the paging rules live on the server
 * (site_family_no_delivery in Sorunlar). Site keys that contain an e-mail
 * arrive already tokenised by the server.
 */
/**
 * Phones, short landscape screens and touch tablets: the expanded Site ×
 * domain list flows in the page there (dashboard.mobile.css), so its only
 * toggle sits at the bottom of a 1-2k px list.
 */
const SITE_SPLIT_FLOW_MEDIA = '(max-width: 720px), (max-height: 520px), (hover: none) and (pointer: coarse)';

/**
 * View only: collapsing that flowed list removes the rows above the viewport
 * while the scroll position stays, which left the card far above the screen.
 * Bring the card's top back under the app header (scroll-margin in
 * dashboard.mobile.css; instant under reduced motion) and keep keyboard focus
 * on the toggle. A card already on screen and mouse desktops (bounded box)
 * are left as they are.
 */
function revealCollapsedSplit(card: HTMLElement | null): void {
  if (!card) return;
  if (typeof window.matchMedia !== 'function' || !window.matchMedia(SITE_SPLIT_FLOW_MEDIA).matches) return;
  const clearTop = Number.parseFloat(getComputedStyle(card).scrollMarginTop) || 0;
  if (card.getBoundingClientRect().top < clearTop && typeof card.scrollIntoView === 'function') {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
  card.querySelector<HTMLElement>('[data-testid="ov-site-split-toggle"]')?.focus({ preventScroll: true });
}

function SiteDomainTable({ split }: { readonly split: readonly OverviewSiteFamilyDelivery[] }): JSX.Element {
  const rows = useMemo(() => siteDomainRows(split), [split]);
  const [showAll, setShowAll] = useState(false);
  const bodyId = useId();
  const splitRef = useRef<HTMLDivElement>(null);
  const [collapseTick, setCollapseTick] = useState(0);
  useLayoutEffect(() => {
    if (collapseTick > 0) revealCollapsedSplit(splitRef.current);
  }, [collapseTick]);
  // Derived, not stored: after a refresh that leaves ≤ SITE_SPLIT_TOP rows the
  // toggle is gone, so the list is back to the default top view (no scroll box
  // or stale aria state), and it does not come back pre-expanded.
  const expandable = rows.length > SITE_SPLIT_TOP;
  const expanded = showAll && expandable;
  useEffect(() => {
    if (!expandable) setShowAll(false);
  }, [expandable]);
  const shown = expanded ? rows : rows.slice(0, SITE_SPLIT_TOP);
  return (
    <div className="ov-split" data-testid="ov-site-split" ref={splitRef}>
      <div className="ov-split-head">
        <h3 className="ov-split-title">
          <Icon.Globe size={14} /> Site × domain (24 saat)
        </h3>
        {rows.length > 0 ? (
          <span className="ov-muted ov-xs" data-testid="ov-site-split-count">
            {n(rows.length)} satır · en kötü oran önce
          </span>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <div className="ov-split-empty ov-muted ov-xs" role="status" data-testid="ov-site-split-empty">
          Son 24 saat için site × domain verisi yok: biten sipariş olmadı ya da sunucu bu kırılımı göndermedi.
        </div>
      ) : (
        <>
          <div
            className={expanded ? 'ov-split-body ov-split-body--all' : 'ov-split-body'}
            id={bodyId}
            // Expanded, the list scrolls inside its own box: keyboard users can focus and scroll it.
            {...(expanded ? { role: 'region', 'aria-label': 'Site × domain listesi', tabIndex: 0 } : {})}
          >
            <Table dense className="ov-split-table" aria-label="Site × domain teslim oranı, son 24 saat">
              <THead>
                <TRow>
                  <TH scope="col">Site</TH>
                  <TH scope="col">Domain / aile</TH>
                  <TH scope="col" className="ov-num">Bitmiş</TH>
                  <TH scope="col" className="ov-num">Teslim</TH>
                  <TH scope="col" className="ov-num">Oran</TH>
                </TRow>
              </THead>
              <TBody>
                {shown.map((r) => (
                  <SiteDomainTableRow key={r.key} row={r} />
                ))}
              </TBody>
            </Table>
          </div>
          {/* The server sends only its busiest site × family pairs (cap not on the wire). */}
          <p className="ov-split-note ov-muted ov-xs" data-testid="ov-site-split-note">
            Liste, sunucunun gönderdiği en yoğun site × aile çiftleriyle sınırlı; burada görünmeyen bir çift
            sağlıklı demek değil.
          </p>
          {expandable ? (
            <div className="ov-split-foot">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (expanded) setCollapseTick((t) => t + 1);
                  setShowAll(!expanded);
                }}
                aria-expanded={expanded}
                aria-controls={bodyId}
                data-testid="ov-site-split-toggle"
              >
                {expanded ? `İlk ${SITE_SPLIT_TOP} satırı göster` : `Tümünü göster (${n(rows.length)})`}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function SiteDomainTableRow({ row }: { readonly row: SiteDomainRow }): JSX.Element {
  const family = providerLabel(row.providerId);
  const rateClass = row.lowSample
    ? 'ov-muted'
    : row.ratePct >= 60
      ? 'ov-good'
      : row.ratePct >= 30
        ? 'ov-split-warn'
        : 'ov-bad';
  const lastHour =
    row.lastHour && row.lastHour.finished > 0
      ? `Son 1 saat: ${n(row.lastHour.delivered)} / ${n(row.lastHour.finished)} teslim`
      : undefined;
  return (
    // Phones (dashboard.mobile.css): the row becomes a three-line card — site +
    // rate, then the domain, then Bitmiş/Teslim; `label` (data-label) names the
    // numbers. The "az örnek" separator is its own span so the phone layout can
    // drop it when the note moves under the rate (cell text is unchanged).
    // data-last-hour repeats the rate's hover tooltip so touch screens (no
    // hover) can print it as a muted line (CSS content, not cell text): under
    // the phone card (row) and under the site in the touch-tablet table (cell).
    <TRow data-testid="ov-site-split-row" data-rate={row.ratePct} data-last-hour={lastHour}>
      <TD className="ov-split-site" label="Site" data-last-hour={lastHour}>{row.site}</TD>
      <TD className="ov-split-domain" label="Domain / aile">
        <span className={row.rest ? 'ov-muted' : 'ov-mono'}>{row.domain}</span>
        {row.rest || family !== row.domain ? <span className="ov-muted ov-xs"> · {family} ailesi</span> : null}
      </TD>
      <TD className="ov-num t-num ov-split-fin" label="Bitmiş">{n(row.finished)}</TD>
      <TD className="ov-num t-num ov-split-del" label="Teslim">{n(row.delivered)}</TD>
      <TD className="ov-num t-num ov-split-rate" label="Oran">
        <span className={rateClass} title={lastHour}>
          %{row.ratePct}
        </span>
        {row.lowSample ? (
          <span className="ov-muted ov-xs ov-split-low">
            <span className="ov-split-sep"> · </span>az örnek
          </span>
        ) : null}
      </TD>
    </TRow>
  );
}

function GatewayRow({ gateway }: { readonly gateway: OverviewGateway }): JSX.Element {
  const tone: Tone = gateway.status === 'ok' ? 'ok' : gateway.status === 'down' ? 'critical' : 'unknown';
  const since =
    gateway.status === 'down' && gateway.since
      ? ` · ${ago(gateway.since)} başladı`
      : gateway.status === 'ok' && gateway.lastOkAt
        ? ` · son başarı ${ago(gateway.lastOkAt)}`
        : '';
  return (
    <li className="ov-gateway" data-testid="ov-gateway" data-status={gateway.status}>
      <Dot tone={tone} />
      <div className="ov-gateway-body">
        <div>
          <strong>{providerLabels(gateway.providerIds) || '—'}</strong>{' '}
          <span className={`ov-gw-status ov-gw-${gateway.status}`}>{GATEWAY_STATUS_LABEL[gateway.status]}</span>
        </div>
        {/* Touch screens print the tooltip's signal source as a muted last
            line (shared [data-touch-detail]); the text content is unchanged. */}
        <div
          className="ov-muted ov-xs"
          title={`Kaynak: ${GATEWAY_SOURCE_LABEL[gateway.source]}`}
          data-touch-detail={`Kaynak: ${GATEWAY_SOURCE_LABEL[gateway.source]}`}
        >
          <span className="ov-mono">
            {gateway.host}
            {gateway.port > 0 ? `:${gateway.port}` : ''}
          </span>
          {since}
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// 2) Sağlayıcılar
// ---------------------------------------------------------------------------

function ProvidersGrid({
  providers,
  canManage,
  onRetry,
}: {
  readonly providers: readonly OverviewProvider[];
  readonly canManage: boolean;
  readonly onRetry: (providerId: string) => void;
}): JSX.Element {
  const sorted = useMemo(() => sortProviders(providers), [providers]);
  const onSale = providers.filter((p) => p.onSale).length;
  return (
    <section className="ov-section" aria-labelledby="ov-providers-title">
      <div className="ov-section-head">
        <h2 id="ov-providers-title" className="ov-section-title">
          Sağlayıcılar
        </h2>
        <span className="ov-muted ov-xs">
          {n(providers.length)} sağlayıcı · {n(onSale)} satışta
        </span>
      </div>
      {sorted.length === 0 ? (
        <EmptyState label="Kayıtlı sağlayıcı yok." testId="ov-providers-empty" />
      ) : (
        <div className="ov-providers" data-testid="ov-providers">
          {sorted.map((p) => (
            <ProviderCard key={p.providerId} provider={p} canManage={canManage} onRetry={onRetry} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProviderCard({
  provider: p,
  canManage,
  onRetry,
}: {
  readonly provider: OverviewProvider;
  readonly canManage: boolean;
  readonly onRetry: (providerId: string) => void;
}): JSX.Element {
  const label = providerLabel(p.providerId, p.label);
  const kind = p.kind;
  const topReason = p.quarantineReasons[0] ?? null;
  const reasonLabel = topReason ? loginFailureLabel(topReason.code) : null;
  const stockGap = p.stock.displayed > p.stock.sellable;
  const stockTone: RatioBarTone =
    p.stock.displayed === 0 ? 'neutral' : p.stock.sellable === 0 ? 'danger' : stockGap ? 'warn' : 'success';
  const showRetry = kind === 'mailbox' && canManage && p.accounts.loginFailed > 0;
  const accountsExtra = [
    kind === 'mailbox' && p.accounts.pending > 0 ? `${n(p.accounts.pending)} bekliyor` : null,
    p.accounts.disabled > 0 ? `${n(p.accounts.disabled)} kapalı` : null,
    p.accounts.suspended > 0 ? `${n(p.accounts.suspended)} askıda` : null,
    p.accounts.noCredential > 0 ? `${n(p.accounts.noCredential)} şifresiz` : null,
  ].filter((x): x is string => x !== null);

  return (
    <article
      className={`ov-prov ov-prov-${p.status}`}
      data-testid={`ov-provider-${p.providerId}`}
      data-status={p.status}
    >
      <header className="ov-prov-head">
        <Dot tone={p.status} />
        <h3 className="ov-prov-title">
          <Link to={providerPath(p.providerId)} className="ov-prov-link">
            {label}
          </Link>
        </h3>
        <Badge tone={p.onSale ? 'success' : 'neutral'}>{p.onSale ? 'Satışta' : 'Satışta değil'}</Badge>
      </header>
      <div className="ov-prov-sub">
        <span className={`ov-status-text ov-status-${p.status}`}>{PROVIDER_STATUS_LABEL[p.status]}</span>
        {' · '}
        {kind === 'alias' ? 'Parent/Alias' : 'Mailbox'}
        {p.activeDomains > 0 ? ` · ${n(p.activeDomains)} domain` : ''}
      </div>

      <dl className="ov-prov-rows">
        <div className="ov-prov-row">
          <dt>Stok</dt>
          <dd>
            {p.stock.displayed === 0 && p.stock.sellable === 0 ? (
              <span className="ov-muted">Stok yok</span>
            ) : (
              <>
                <span>
                  <strong className="t-num">{n(p.stock.sellable)}</strong> satılabilir
                </span>
                <RatioBar
                  value={p.stock.sellable}
                  total={p.stock.displayed}
                  tone={stockTone}
                  label="Satılabilir / gösterilen"
                  height={5}
                />
                <span className="ov-muted ov-xs">
                  {n(p.stock.displayed)} gösteriliyor
                  {p.stock.inFlight > 0 ? ` · ${n(p.stock.inFlight)} kullanımda` : ''}
                </span>
              </>
            )}
          </dd>
        </div>

        <div className="ov-prov-row">
          <dt>Hesaplar</dt>
          <dd>
            <span>
              {kind === 'alias' ? (
                <>
                  <strong className="t-num">{n(p.accounts.active)}</strong> aktif
                </>
              ) : (
                <>
                  <strong className="t-num">{n(p.accounts.verified)}</strong> doğrulanmış
                </>
              )}
              <span className="ov-muted"> / {n(p.accounts.total)}</span>
            </span>
            {accountsExtra.length > 0 ? <span className="ov-muted ov-xs">{accountsExtra.join(' · ')}</span> : null}
            {kind === 'mailbox' && p.accounts.lastVerifiedAt ? (
              <span className="ov-muted ov-xs">Son doğrulama {ago(p.accounts.lastVerifiedAt)}</span>
            ) : null}
          </dd>
        </div>

        <div className="ov-prov-row ov-prov-row--quarantine">
          <dt>Karantina</dt>
          <dd data-testid={`ov-provider-${p.providerId}-quarantine`}>
            {p.accounts.loginFailed > 0 ? (
              <>
                <span>
                  <Badge tone={p.accounts.sticky > 0 ? 'danger' : 'warn'}>
                    {n(p.accounts.loginFailed)} hatalı giriş
                  </Badge>
                  {p.accounts.sticky > 0 ? (
                    <span className="ov-bad ov-xs"> {n(p.accounts.sticky)} kalıcı</span>
                  ) : null}
                </span>
                {reasonLabel && topReason ? (
                  <span className="ov-xs ov-reason" title={reasonLabel.hint || undefined} data-touch-detail={reasonLabel.hint || undefined}>
                    {reasonLabel.label}
                    {p.quarantineReasons.length > 1 ? ` (+${p.quarantineReasons.length - 1} neden)` : ''}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="ov-muted">Yok</span>
            )}
          </dd>
        </div>

        <div className="ov-prov-row">
          <dt>Siparişler 24s</dt>
          <dd>
            <span>
              <strong className="t-num">{n(p.orders24h.delivered)}</strong> teslim
              <span className="ov-muted"> / {n(p.orders24h.total)}</span>
              {p.orders24h.cancelled > 0 ? <span className="ov-muted ov-xs"> · {n(p.orders24h.cancelled)} iptal</span> : null}
            </span>
            <span className="ov-muted ov-xs">
              7 gün: {n(p.orders7d.delivered)} / {n(p.orders7d.total)}
            </span>
          </dd>
        </div>

        <div className="ov-prov-row">
          <dt>Son teslim</dt>
          <dd>{p.lastDeliveryAt ? ago(p.lastDeliveryAt) : <span className="ov-muted">—</span>}</dd>
        </div>

        {p.renewal24h ? (
          <div className="ov-prov-row">
            <dt>Yenileme 24s</dt>
            <dd>
              <span>
                <span className="t-num">{n(p.renewal24h.completed)}</span> tamamlandı
                {' · '}
                <span className={p.renewal24h.failed > 0 ? 'ov-bad' : undefined}>
                  <span className="t-num">{n(p.renewal24h.failed)}</span> başarısız
                </span>
              </span>
            </dd>
          </div>
        ) : null}

        {p.gateway ? (
          <div className="ov-prov-row">
            <dt>Ağ geçidi</dt>
            <dd>
              <span className={`ov-gw-status ov-gw-${p.gateway.status}`}>{GATEWAY_STATUS_LABEL[p.gateway.status]}</span>
              {p.gateway.since && p.gateway.status === 'down' ? (
                <span className="ov-muted ov-xs">{ago(p.gateway.since)} başladı</span>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>

      {showRetry ? (
        <div className="ov-prov-actions">
          <Button
            size="sm"
            icon={<Icon.Refresh size={13} />}
            onClick={() => onRetry(p.providerId)}
            data-testid={`ov-provider-${p.providerId}-retry`}
          >
            Sorunluları tekrar dene ({n(p.accounts.loginFailed)})
          </Button>
        </div>
      ) : null}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Retry modal (dry run → confirm)
// ---------------------------------------------------------------------------

function RetryModal({
  flow,
  attempts,
  onAttempts,
  onClose,
  onConfirm,
  provider,
  gatewayDown,
}: {
  readonly flow: RetryFlow;
  readonly attempts: 1 | 2 | 3;
  readonly onAttempts: (a: 1 | 2 | 3) => void;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly provider: OverviewProvider | null;
  readonly gatewayDown: boolean;
}): JSX.Element | null {
  if (flow.step === 'closed') return null;
  const label = providerLabel(flow.providerId, provider?.label);
  const preview = flow.step === 'preview' || flow.step === 'applying' ? flow.preview : flow.step === 'error' ? flow.preview : null;
  const canConfirm = flow.step === 'preview' && flow.preview.candidates > 0;
  // Only the confirm POST (a write) locks the modal. The dry run changes
  // nothing and has no client deadline, so it stays cancellable (X / Esc /
  // Vazgeç); the page ignores its late answer via the retry token.
  const locked = flow.step === 'applying';
  const tenMinus = 10 - attempts;
  return (
    <Modal
      open
      onClose={locked ? () => undefined : onClose}
      title={`${label}: Sorunluları tekrar dene`}
      width={520}
      testId="ov-retry-modal"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={locked} data-testid="ov-retry-cancel">
            Vazgeç
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!canConfirm}
            loading={flow.step === 'applying'}
            data-testid="ov-retry-confirm"
          >
            {preview && preview.candidates > 0 ? `${n(preview.candidates)} hesabı tekrar dene` : 'Tekrar dene'}
          </Button>
        </>
      }
    >
      {flow.step === 'loading' ? (
        <LoadingState label="Adaylar sunucuda hesaplanıyor…" />
      ) : (
        <div className="ov-retry">
          {flow.step === 'error' ? (
            <Banner tone="danger" testId="ov-retry-error">
              {flow.message}
            </Banner>
          ) : null}
          {preview ? (
            <>
              <ul className="ov-kv" data-testid="ov-retry-preview">
                <li>
                  <span>Tekrar denenecek hesap</span>
                  <strong className="t-num" data-testid="ov-retry-candidates">
                    {n(preview.candidates)}
                  </strong>
                </li>
                <li>
                  <span>Bunlardan kalıcı karantinada (10 deneme sınırı)</span>
                  <strong className="t-num">{n(preview.sticky)}</strong>
                </li>
                <li>
                  <span>Kullanımda olduğu için atlanacak</span>
                  <strong className="t-num">{n(preview.skippedInFlight)}</strong>
                </li>
                <li>
                  <span>Kayıtlı şifresi olmadığı için denenemeyecek</span>
                  <strong className="t-num">{n(preview.noCredential)}</strong>
                </li>
              </ul>
              {preview.candidates === 0 ? (
                <Banner tone="info">Şu an tekrar denenebilecek hesap yok.</Banner>
              ) : (
                <>
                  <label className="ov-field">
                    <span>Hesap başına yeni deneme hakkı</span>
                    <Select
                      value={String(attempts)}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        onAttempts(v === 1 ? 1 : v === 2 ? 2 : 3);
                      }}
                      disabled={flow.step !== 'preview'}
                      aria-label="Hesap başına yeni deneme hakkı"
                      data-testid="ov-retry-attempts"
                    >
                      <option value="1">1 deneme</option>
                      <option value="2">2 deneme</option>
                      <option value="3">3 deneme</option>
                    </Select>
                  </label>
                  <p className="ov-muted ov-xs ov-retry-help" data-testid="ov-retry-help">
                    Sunucu seçilen hesapların bekleme süresini sıfırlar ve hata sayacını en çok {tenMinus} olacak
                    şekilde geri çeker: her hesap en az {attempts} yeni deneme hakkı alır (kalıcı karantinadakiler
                    tam {attempts}). Yine reddedilen hesap tekrar karantinaya düşer. Sonuç ~1–15 dk içinde görünür.
                    İşlem denetim kaydına yazılır ve tekrar çalıştırmak güvenlidir.
                  </p>
                  {gatewayDown ? (
                    <Banner tone="warn" testId="ov-retry-gateway-down">
                      Bu sağlayıcının ağ geçidi şu an çalışmıyor: bağlantı kurulamayan denemeler hakkı harcamaz,
                      ağ geçidi düzelince yapılır.
                    </Banner>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 3) Hızlı erişim
// ---------------------------------------------------------------------------

const QUICK_LINKS: readonly {
  readonly to: string;
  readonly label: string;
  readonly capability: AdminCapability;
  readonly icon: IconName;
}[] = [
  { to: '/users', label: 'Kullanıcılar', capability: 'user:list', icon: 'Users' },
  { to: '/orders', label: 'Siparişler', capability: 'order:inspect', icon: 'Inbox' },
  { to: '/financial-reviews', label: 'Ödeme incelemeleri', capability: 'financial:review', icon: 'Coins' },
  { to: '/maintenance', label: 'Bakım modu', capability: 'maintenance:view', icon: 'Settings' },
  { to: '/activity', label: 'Son aktiviteler', capability: 'activity:read', icon: 'Clock' },
  { to: '/alerts', label: 'Uyarılar', capability: 'observability:read', icon: 'Warn' },
  { to: '/providers', label: 'Sağlayıcı sağlığı', capability: 'provider:view_health', icon: 'Globe' },
];

function QuickLinks(): JSX.Element | null {
  const session = useSession();
  const links = QUICK_LINKS.filter((l) => session.has(l.capability));
  if (links.length === 0) return null;
  return (
    <section className="ov-section" aria-labelledby="ov-quick-title">
      <div className="ov-section-head">
        <h2 id="ov-quick-title" className="ov-section-title">
          Hızlı erişim
        </h2>
      </div>
      <nav className="ov-quick" aria-label="Hızlı erişim" data-testid="ov-quick">
        {links.map((l) => {
          const I = Icon[l.icon];
          return (
            <Link key={l.to} to={l.to} className="ov-quick-link">
              <I size={14} />
              {l.label}
            </Link>
          );
        })}
      </nav>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4) Sorunlar — rendered LAST: one compact, scrollable panel with filters
// ---------------------------------------------------------------------------

const SEVERITY_FILTERS: readonly { readonly key: SeverityFilter; readonly label: string }[] = [
  { key: 'all', label: 'Tümü' },
  { key: 'critical', label: 'Kritik' },
  { key: 'warn', label: 'Uyarı' },
  { key: 'info', label: 'Bilgi' },
];

function matchesProvider(p: OverviewProblem, filter: string): boolean {
  if (filter === ALL_PROVIDERS) return true;
  if (filter === GENERAL_FILTER) return p.providerId === null;
  return p.providerId === filter;
}

function matchesSeverity(p: OverviewProblem, filter: SeverityFilter): boolean {
  return filter === 'all' || p.severity === filter;
}

interface ProviderOption {
  readonly value: string;
  readonly label: string;
  readonly count: number;
}

function ProblemsPanel({
  problems,
  providersById,
  canManage,
  onRetry,
  severity,
  onSeverity: setSeverity,
  providerFilter,
  onProviderFilter: setProviderFilter,
}: {
  readonly problems: readonly OverviewProblem[];
  readonly providersById: ReadonlyMap<string, OverviewProvider>;
  readonly canManage: boolean;
  readonly onRetry: (providerId: string) => void;
  readonly severity: SeverityFilter;
  readonly onSeverity: (s: SeverityFilter) => void;
  readonly providerFilter: string;
  readonly onProviderFilter: (providerId: string) => void;
}): JSX.Element {
  const sorted = useMemo(() => sortProblems(problems), [problems]);
  const critical = problems.filter((p) => p.severity === 'critical').length;
  const listRef = useRef<HTMLDivElement>(null);
  // A new filter starts the list at its top (never mid-list of another view);
  // the 30s refresh keeps both the filters and the scroll position.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [severity, providerFilter]);

  // Faceted counts: the severity chips count inside the chosen provider and
  // the provider options count inside the chosen severity, so every number
  // is exactly what picking it would show.
  const byProvider = useMemo(() => sorted.filter((p) => matchesProvider(p, providerFilter)), [sorted, providerFilter]);
  const severityCounts = useMemo(() => {
    const c: Record<SeverityFilter, number> = { all: byProvider.length, critical: 0, warn: 0, info: 0 };
    for (const p of byProvider) c[p.severity] += 1;
    return c;
  }, [byProvider]);
  const visible = useMemo(() => byProvider.filter((p) => matchesSeverity(p, severity)), [byProvider, severity]);
  const allProvidersCount = useMemo(() => sorted.filter((p) => matchesSeverity(p, severity)).length, [sorted, severity]);

  const providerOptions = useMemo((): readonly ProviderOption[] => {
    const counts = new Map<string, number>();
    let hasGeneral = providerFilter === GENERAL_FILTER;
    let general = 0;
    for (const p of sorted) {
      const hit = matchesSeverity(p, severity) ? 1 : 0;
      if (p.providerId === null) {
        hasGeneral = true;
        general += hit;
      } else {
        counts.set(p.providerId, (counts.get(p.providerId) ?? 0) + hit);
      }
    }
    // A chosen provider whose problems all cleared on a refresh stays
    // selectable (count 0 → "Bu filtrede sorun yok"), never silently reset.
    if (providerFilter !== ALL_PROVIDERS && providerFilter !== GENERAL_FILTER && !counts.has(providerFilter)) {
      counts.set(providerFilter, 0);
    }
    const list: ProviderOption[] = [...counts]
      .map(([id, count]) => ({ value: id, label: providerLabel(id, providersById.get(id)?.label), count }))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    if (hasGeneral) list.push({ value: GENERAL_FILTER, label: 'Genel', count: general });
    return list;
  }, [sorted, severity, providerFilter, providersById]);

  const resetFilters = (): void => {
    setSeverity('all');
    setProviderFilter(ALL_PROVIDERS);
  };

  return (
    <section
      id={PROBLEMS_ANCHOR}
      className="ov-section ov-problems-section"
      aria-labelledby="ov-problems-title"
      tabIndex={-1}
    >
      <div className="ov-pp" data-testid="ov-problems-panel">
        <div className="ov-pp-head">
          <div className="ov-pp-titlebar">
            <h2 id="ov-problems-title" className="ov-section-title">
              Sorunlar
            </h2>
            {problems.length > 0 ? (
              <Badge tone={critical > 0 ? 'danger' : 'warn'} data-testid="ov-problem-count">
                {critical > 0 ? `${n(critical)} kritik · ${n(problems.length)} toplam` : n(problems.length)}
              </Badge>
            ) : null}
          </div>
          {problems.length > 0 ? (
            <div className="ov-pp-filters">
              <div className="ov-pp-chips" role="group" aria-label="Önem derecesine göre süz">
                {SEVERITY_FILTERS.map((f) => (
                  <Button
                    key={f.key}
                    size="sm"
                    variant="ghost"
                    className={`ov-chip ov-chip-${f.key}`}
                    aria-pressed={severity === f.key}
                    onClick={() => setSeverity(f.key)}
                    data-testid={`ov-filter-${f.key}`}
                  >
                    {f.label}{' '}
                    <span className="ov-chip-count t-num">{n(severityCounts[f.key])}</span>
                  </Button>
                ))}
              </div>
              <Select
                className="ov-pp-provider"
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                aria-label="Sağlayıcıya göre süz"
                data-testid="ov-filter-provider"
              >
                <option value={ALL_PROVIDERS}>Tüm sağlayıcılar ({n(allProvidersCount)})</option>
                {providerOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} ({n(o.count)})
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>

        {sorted.length === 0 ? (
          <div className="ov-problems-empty" role="status" data-testid="ov-problems-empty">
            <Icon.CheckCircle size={18} />
            <div>
              <strong>Sorun yok</strong>
              <span className="ov-muted"> — sağlayıcılar, siparişler ve proxy'ler beklenen durumda.</span>
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div className="ov-pp-filter-empty" role="status" data-testid="ov-problems-filter-empty">
            <span>Bu filtrede sorun yok.</span>
            <Button size="sm" variant="ghost" onClick={resetFilters} data-testid="ov-filter-reset">
              Tüm sorunları göster
            </Button>
          </div>
        ) : (
          // Keyboard-focusable scroll region: arrow keys / PgDn scroll the list.
          <div
            ref={listRef}
            id={PROBLEMS_LIST_ID}
            className="ov-pp-body"
            role="region"
            aria-label="Sorun listesi"
            tabIndex={0}
            data-testid="ov-problems-scroll"
          >
            <ul className="ov-problems" data-testid="ov-problems">
              {visible.map((p) => (
                <ProblemRow
                  key={p.id}
                  problem={p}
                  providerLabelText={
                    p.providerId ? providerLabel(p.providerId, providersById.get(p.providerId)?.label) : null
                  }
                  canManage={canManage}
                  onRetry={onRetry}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

const SEVERITY_BADGE: Record<OverviewSeverity, BadgeTone> = { critical: 'danger', warn: 'warn', info: 'info' };

function ProblemRow({
  problem,
  providerLabelText,
  canManage,
  onRetry,
}: {
  readonly problem: OverviewProblem;
  readonly providerLabelText: string | null;
  readonly canManage: boolean;
  readonly onRetry: (providerId: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const session = useSession();
  const text = describeProblem(problem.code, problem.params, problem.providerId);
  const linkCap = problem.action?.kind === 'link' ? linkCapability(problem.action.href) : null;
  // e.g. customer_low_delivery → /users/<id> needs user:view_detail: no "Aç" without it.
  const action = linkCap !== null && !session.has(linkCap) ? null : problem.action;
  // Titles that already lead with "<Sağlayıcı>: " skip the badge. A prefix
  // check, not `includes`: a site title such as "gmail.com siparişlerinde…"
  // contains "mail.com" but still needs the provider badge.
  const showProviderBadge = providerLabelText !== null && !text.title.startsWith(`${providerLabelText}:`);
  return (
    <li
      className={`ov-problem ov-sev-${problem.severity}`}
      data-testid={`ov-problem-${problem.id}`}
      data-severity={problem.severity}
    >
      <div className="ov-problem-body">
        <div className="ov-problem-title">
          <Badge tone={SEVERITY_BADGE[problem.severity]}>{SEVERITY_LABEL[problem.severity]}</Badge>
          {showProviderBadge ? (
            <Badge tone="neutral" data-testid={`ov-problem-provider-${problem.id}`}>
              {providerLabelText}
            </Badge>
          ) : null}
          <span className="ov-problem-text" title={text.title}>
            {text.title}
          </span>
          {problem.since ? <span className="ov-problem-since">{ago(problem.since)} başladı</span> : null}
        </div>
        <ProblemDescription text={text.description} testId={`ov-problem-more-${problem.id}`} />
      </div>
      {action ? (
        <div className="ov-problem-action">
          {action.kind === 'retry_problematic' ? (
            canManage ? (
              <Button
                size="sm"
                variant="primary"
                icon={<Icon.Refresh size={13} />}
                onClick={() => onRetry(action.providerId)}
                data-testid={`ov-problem-retry-${problem.id}`}
              >
                Sorunluları tekrar dene
              </Button>
            ) : null
          ) : action.href.startsWith('/') ? (
            <Button size="sm" iconRight={<Icon.ChevronRight size={12} />} onClick={() => navigate(action.href)}>
              Aç
            </Button>
          ) : (
            <a className="btn btn-secondary btn-sm" href={action.href} target="_blank" rel="noreferrer">
              Aç <Icon.ArrowUR size={12} />
            </a>
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Description clamped to two lines. The "Devamı" toggle appears only when the
 * text really overflows the clamp (measured, re-measured on resize), so short
 * sentences never carry a useless button.
 */
function ProblemDescription({ text, testId }: { readonly text: string; readonly testId: string }): JSX.Element {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    if (expanded) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const measure = (): void => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, text]);

  return (
    <>
      <div id={id} ref={ref} className={`ov-problem-desc${expanded ? '' : ' ov-clamp'}`}>
        {text}
      </div>
      {overflows || expanded ? (
        <Button
          size="sm"
          variant="ghost"
          className="ov-problem-more"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((v) => !v)}
          data-testid={testId}
        >
          {expanded ? 'Daha az' : 'Devamı'}
        </Button>
      ) : null}
    </>
  );
}
